import React, { useState, ChangeEvent, useEffect, useContext } from "react";
import { Modal, Box } from "@mui/material";
import CustomInput from "../shared/custom-input/custom-input";
import CustomButton from "../shared/custom-button/custom-button";
import { AddAPhoto as AddAPhotoIcon } from "@mui/icons-material";
import { getAllTeacherFromSuperTeacher } from "../services/super-teacher";
import { getUserGroupService } from "../services/group-service";
import { RootState } from "../redux/store/store";
import { useSelector } from "react-redux";
import { SnackbarContext } from "../config/hooks/use-toast";
import CustomAutocomplete from "../shared/custom-autoComplete/custom-autocomplete";

import { faCamera, faTimes, faBook, faGraduationCap, faUsers, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Subject {
  id?: number;
  backgroundImageUrl?: string;
  teacherId?: string;
  level: string;
  speciality: string;
  groupIds?: number[];
}

interface GroupOption {
  value: number;
  label: string;
}

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  initialData?: Subject;
  defaultGroupId?: number;
  isEdit?: boolean;
  modalTitle: string;
  buttonText: string;
  onButtonClick: (formData: any) => void;
}

const defaultData: Subject = {
  level: "",
  speciality: "",
  teacherId: "",
};

const SubjectModal: React.FC<ProfileModalProps> = ({
  open,
  onClose,
  initialData = defaultData,
  defaultGroupId,
  isEdit = false,
  modalTitle,
  buttonText,
  onButtonClick,
}) => {
  const snackbarContext = useContext(SnackbarContext);

  const role = useSelector((state: RootState) => state?.user?.userData?.role.name);
  const [backgroundImageFile, setBackgroundImageFile] = useState<File | null>(null);
  const id = useSelector((state: RootState) => state?.user?.userData?.id);
  const name = useSelector((state: RootState) => state?.user?.userData?.fullName);
  
  const [teachersArray, setTeachers] = useState<any[]>([]);
  const [availableGroups, setAvailableGroups] = useState<GroupOption[]>([]);
  const [formData, setFormData] = useState<Subject>(initialData ?? defaultData);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);

  useEffect(() => {
    if (open && (role === "ROLE_ADMIN" || role === "ROLE_SUPER_TEACHER")) {
      getAllTeacherFromSuperTeacher()
        .then((res) => {
          const teachers = res.data.map((item: { fullName: any; id: any }) => ({
            label: item.fullName,
            value: item.id,
          }));
          teachers.push({ label: name, value: id });
          setTeachers(teachers);
        });

      getUserGroupService()
        .then((res) => {
          const options: GroupOption[] = res.data.map((group: any) => ({
            label: group.title,
            value: group.id,
          }));
          setAvailableGroups(options);
        });
    }
  }, [open, role, id, name]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      if (initialData.groupIds && initialData.groupIds.length > 0) {
        setSelectedGroupIds(initialData.groupIds.map(Number));
      } else if (defaultGroupId) {
        setSelectedGroupIds([defaultGroupId]);
      }
    } else {
      setFormData(defaultData);
      if (defaultGroupId) {
        setSelectedGroupIds([defaultGroupId]);
      } else {
        setSelectedGroupIds([]);
      }
    }
  }, [initialData, defaultGroupId, open]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        backgroundImageUrl: URL.createObjectURL(file),
      }));
      setBackgroundImageFile(file);
    }
  };

  const handleGroupSelection = (groupId: number) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
    );
  };

  const handleActionClick = () => {
    const preparedFormData = new FormData();
    const PreparedJson = {
      speciality: formData.speciality,
      level: formData.level,
      teacherId: formData?.teacherId ? formData.teacherId : id,
    };
  
    if (!PreparedJson.speciality || !PreparedJson.level) {
      if (snackbarContext) {
        snackbarContext.showMessage("Erreur", "Veuillez remplir les champs obligatoires", "error");
      }
      return;
    }

    if (selectedGroupIds.length === 0) {
      if (snackbarContext) {
        snackbarContext.showMessage("Erreur", "Veuillez sélectionner au moins un groupe", "error");
      }
      return;
    }
  
    preparedFormData.append("subjectDTOJson", JSON.stringify(PreparedJson));
    if (backgroundImageFile) {
      preparedFormData.append("backgroundImage", backgroundImageFile);
    }
    if (!backgroundImageFile && !formData.backgroundImageUrl) {
      if (snackbarContext) {
        snackbarContext.showMessage("Erreur", "Veuillez importer une image de couverture", "error");
      }
      return;
    }

    selectedGroupIds.forEach((groupIdValue) => {
      preparedFormData.append("groupIds", groupIdValue.toString());
    });
  
    onButtonClick(preparedFormData);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="flex justify-end backdrop-blur-sm bg-title/20"
      closeAfterTransition
    >
      <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        
        {/* Drawer Header */}
        <div className="p-10 pb-6 flex items-center justify-between border-b border-gray-50 bg-white/50 backdrop-blur-md flex-shrink-0 z-30">
          <div>
            <h2 className="text-3xl font-black text-title uppercase tracking-tighter">{modalTitle}</h2>
            <p className="text-[10px] font-black text-primary uppercase tracking-[0.4em] mt-1.5">{isEdit ? "Édition de la matière" : "Nouvelle ressource"}</p>
          </div>
          <button 
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gray-50 text-text/30 hover:bg-rose-50 hover:text-rose-500 transition-all duration-300 group cursor-pointer"
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg group-hover:rotate-90 transition-transform duration-500 pointer-events-none" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar p-10 space-y-12">
          
          {/* Section: Illustration */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
              Illustration de la matière
            </h3>

            <label className="group relative block w-full aspect-[16/9] rounded-[2.5rem] overflow-hidden cursor-pointer bg-slate-50 border-2 border-dashed border-slate-100 hover:border-primary/40 transition-all duration-700">
              {formData.backgroundImageUrl ? (
                <>
                  <img src={formData.backgroundImageUrl} alt="Preview" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-title/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center">
                    <div className="flex flex-col items-center text-white space-y-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-2xl border border-white/20">
                         <FontAwesomeIcon icon={faCamera} className="text-xl" />
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-[0.2em]">Remplacer l'image</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300">
                  <div className="w-20 h-20 bg-white rounded-3xl shadow-xl shadow-slate-200/50 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-700">
                    <FontAwesomeIcon icon={faCamera} className="text-2xl" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-400">Ajouter un visuel</span>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          </section>

          {/* Section: Subject & Level */}
          <section className="space-y-8">
            <div className="space-y-3">
              <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Désignation de la matière
              </label>
              <CustomInput
                placeholder="Ex: Analyse Mathématique"
                inputType="text"
                CustomStyle="!h-auto !my-0"
                inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all"
                value={formData.speciality}
                name="speciality"
                onChange={handleChange}
              />
            </div>

            <div className="space-y-3">
              <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Niveau d'études
              </label>
              <CustomInput
                placeholder="Ex: Terminale S"
                inputType="text"
                CustomStyle="!h-auto !my-0"
                inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all"
                value={formData.level}
                name="level"
                onChange={handleChange}
              />
            </div>
          </section>

          <section className="space-y-4">
             <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Enseignant Responsable
             </label>
             <CustomAutocomplete
                options={teachersArray}
                value={formData.teacherId}
                onChange={(value) => setFormData({ ...formData, teacherId: value })}
             />
          </section>

          {/* Section: Groups Selection */}
          <section className="space-y-4">
             <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Assignations aux classes
             </label>
             <div className="flex flex-wrap gap-2 p-6 bg-slate-50/50 rounded-[2.5rem] border border-slate-100">
                {availableGroups.length === 0 ? (
                  <p className="text-[10px] font-bold text-slate-300 uppercase tracking-[0.2em] italic py-4 w-full text-center">Aucune classe disponible</p>
                ) : (
                  availableGroups.map((group) => {
                    const isSelected = selectedGroupIds.includes(group.value);
                    return (
                      <button
                        key={group.value}
                        type="button"
                        onClick={() => handleGroupSelection(group.value)}
                        className={`px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 border ${
                          isSelected
                            ? "bg-primary text-white border-primary shadow-lg shadow-primary/20 scale-105"
                            : "bg-white text-title border-slate-100 hover:border-primary/40 hover:bg-slate-50"
                        }`}
                      >
                        {group.label}
                      </button>
                    );
                  })
                )}
             </div>
          </section>

          <div className="pb-10"></div>
        </div>

        {/* Footer Actions */}
        <div className="p-10 bg-white border-t border-gray-100 flex gap-4 z-40 flex-shrink-0">
           <button 
              onClick={onClose}
              className="px-8 h-16 rounded-2xl font-black text-[11px] uppercase tracking-widest text-text/40 hover:text-rose-500 hover:bg-rose-50 active:scale-95 transition-all duration-300 cursor-pointer"
           >
              <span className="pointer-events-none">Annuler</span>
           </button>
           <button 
              onClick={handleActionClick}
              className="flex-1 h-16 bg-primary text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center justify-center gap-4 group cursor-pointer"
           >
              <span className="pointer-events-none">{buttonText}</span>
              <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500 pointer-events-none">
                 <FontAwesomeIcon icon={faCheck} className="text-[10px]" />
              </div>
           </button>
        </div>
      </div>
    </Modal>
  );
};

export default SubjectModal;
